import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Search, Users, Building2, Shield, ChevronLeft, ChevronRight, Check, Plus, Pencil, Trash2, KeyRound } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULES = [
  { id: 1, name: 'Carpeta de Arranque', color: '#E6A817' },
  { id: 2, name: 'GCOM', color: '#D35400' },
  { id: 3, name: 'Organigrama', color: '#5B7D9A' },
  { id: 4, name: 'Capacitacion', color: '#1B919B' },
  { id: 5, name: 'Control Documental', color: '#6B8E8E' },
  { id: 6, name: 'APP Pase Digital', color: '#6C3D8E' },
  { id: 7, name: 'Planificador HS', color: '#a05e49' },
  { id: 8, name: 'Checklist Digital', color: '#2E7D32' },
  { id: 9, name: 'Centro de Datos', color: '#7C3AED' },
  { id: 10, name: 'Perfil Trabajador', color: '#4A6FA5' },
  { id: 11, name: 'HS Safework', color: '#0E7490' },
  { id: 12, name: 'Repositorio HS', color: '#DB2777' },
];

function ModuleToggle({ userModules, userId, userType, token, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const toggle = async (modId) => {
    const current = [...userModules];
    const newMods = current.includes(modId) ? current.filter(m => m !== modId) : [...current, modId];
    setSaving(true);
    try {
      await axios.put(`${API}/admin/usuarios/${userType}/${userId}/modules`, newMods,
        { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(newMods);
    } catch { toast.error('Error al actualizar modulos'); }
    setSaving(false);
  };
  return (
    <div className="flex gap-0.5 flex-nowrap items-center">
      {MODULES.map(m => {
        const active = userModules.includes(m.id);
        return (
          <button key={m.id} onClick={() => toggle(m.id)} disabled={saving}
            className={`relative w-6 h-6 rounded-md text-[10px] font-bold transition-all shrink-0 ${active ? 'text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
            style={active ? { backgroundColor: m.color } : {}}
            title={m.name} data-testid={`mod-${userId}-${m.id}`}>
            {active ? <Check className="w-3 h-3 mx-auto" /> : m.id}
          </button>
        );
      })}
    </div>
  );
}

function Pagination({ page, totalPages, total, limit, onPageChange, testId }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className="text-xs text-slate-400">{(page - 1) * limit + 1}-{Math.min(page * limit, total)} de {total}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-30">
          <ChevronLeft className="w-3.5 h-3.5" /> Anterior
        </button>
        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
          let p;
          if (totalPages <= 7) p = i + 1;
          else if (page <= 4) p = i + 1;
          else if (page >= totalPages - 3) p = totalPages - 6 + i;
          else p = page - 3 + i;
          return (
            <button key={p} onClick={() => onPageChange(p)}
              className={`w-8 h-8 text-xs rounded-lg ${p === page ? 'bg-[#1B919B] text-white' : 'border border-slate-200 hover:bg-slate-50 text-slate-600'}`}>
              {p}
            </button>
          );
        })}
        <button onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-30">
          Siguiente <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ==================== ESPECIALES TAB ==================== */
function EspecialesTable({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', apellido: '', rut: '', email: '', password: '', rol: 'administrador' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users`, { headers });
      setUsers(res.data.filter(u => u.rol === 'administrador' || u.rol === 'capacitador' || u.rol === 'planificador' || u.rol === 'planificador_liderazgo' || u.rol === 'pases_visita' || u.rol === 'servicios_generales' || u.rol === 'equipo_hs'));
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${u.nombre} ${u.apellido}`.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.rut || '').toLowerCase().includes(q);
  });

  const openNew = () => {
    setEditing(null);
    setForm({ nombre: '', apellido: '', rut: '', email: '', password: '', rol: 'administrador' });
    setShowModal(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ nombre: u.nombre || '', apellido: u.apellido || '', rut: u.rut || '', email: u.email || '', password: '', rol: u.rol });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nombre || !form.email) { toast.error('Nombre y email son obligatorios'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/users/${editing.id}`, { ...form, password: form.password || '1234' }, { headers });
        toast.success('Usuario actualizado');
      } else {
        if (!form.password) { toast.error('Ingrese una clave'); return; }
        await axios.post(`${API}/auth/register`, form, { headers });
        toast.success('Usuario creado');
      }
      setShowModal(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = (u) => {
    setConfirmDelete({
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/users/${u.id}`, { headers });
          toast.success('Usuario desactivado');
          fetchUsers();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmDelete(null);
      }
    });
  };

  const rolBadge = (rol) => {
    if (rol === 'administrador') return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Administrador</span>;
    if (rol === 'planificador') return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Planificador</span>;
    if (rol === 'planificador_liderazgo') return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Planificador HS</span>;
    if (rol === 'pases_visita') return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800">Pases de Visita</span>;
    if (rol === 'servicios_generales') return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">Servicios Generales</span>;
    if (rol === 'equipo_hs') return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">Equipo HS</span>;
    return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">Capacitador</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, RUT o email..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20"
            data-testid="search-especiales" />
        </div>
        <span className="text-xs text-slate-400">{filtered.length} usuario(s)</span>
        <button onClick={openNew} className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] transition-colors" data-testid="nuevo-especial-btn">
          <Plus className="w-4 h-4" /> Nuevo Usuario
        </button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full" data-testid="table-especiales">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">RUT</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Nombre</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Email</th>
              <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Rol</th>
              <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#a05e49] mx-auto" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-400">No se encontraron usuarios</td></tr>
            ) : filtered.map(u => (
              <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`especial-row-${u.id}`}>
                <td className="px-3 py-2 text-xs font-mono text-slate-600">{u.rut || '-'}</td>
                <td className="px-3 py-2 text-xs font-medium text-slate-800">{u.nombre} {u.apellido}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{u.email}</td>
                <td className="px-3 py-2 text-center">{rolBadge(u.rol)}</td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => openEdit(u)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" data-testid={`edit-especial-${u.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(u)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`delete-especial-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar Usuario' : 'Nuevo Usuario Especial'}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Nombre *</label>
                <input type="text" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-nombre" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Apellido</label>
                <input type="text" value={form.apellido} onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-apellido" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">RUT</label>
              <input type="text" value={form.rut} onChange={e => setForm(f => ({ ...f, rut: e.target.value }))}
                placeholder="12.345.678-9" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-rut" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">{editing ? 'Clave (dejar vacio para mantener)' : 'Clave *'}</label>
              <input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder={editing ? '••••••' : ''} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-password" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Rol *</label>
              <select value={form.rol} onChange={e => setForm(f => ({ ...f, rol: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-rol">
                <option value="administrador">Administrador</option>
                <option value="capacitador">Capacitador</option>
                <option value="planificador">Planificador</option>
                <option value="planificador_liderazgo">Planificador HS</option>
                <option value="pases_visita">Pases de Visita</option>
                <option value="servicios_generales">Servicios Generales</option>
                <option value="equipo_hs">Equipo HS</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm bg-[#a05e49] text-white rounded-lg hover:bg-[#8b4f3d]" data-testid="modal-save-btn">{editing ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      , document.body)}

      <ConfirmModal open={!!confirmDelete} title="Desactivar usuario" message="Esta seguro de desactivar este usuario?"
        confirmLabel="Desactivar" variant="danger" onConfirm={confirmDelete?.onConfirm} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
}

/* ==================== USER TABLE (Mandante/Contratista) ==================== */
function UserTable({ userType, token }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [empresas, setEmpresas] = useState([]);
  const [filterEmpresa, setFilterEmpresa] = useState('');
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', apellido: '', rut: '', email: '', clave: '' });
  const [keyUserModal, setKeyUserModal] = useState(null);
  const LIMIT = 8;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (userType === 'contratista') {
      axios.get(`${API}/empresas-mandante2`, { headers }).then(r => setEmpresas(r.data)).catch(() => {});
    }
  }, [userType, token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterEmpresa) params.set('empresa_id', filterEmpresa);
      const res = await axios.get(`${API}/admin/usuarios/${userType}?${params}`, { headers });
      setUsers(res.data.data);
      setTotal(res.data.total);
    } catch {}
    setLoading(false);
  }, [token, userType, page, debouncedSearch, filterEmpresa]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const totalPages = Math.ceil(total / LIMIT);

  const updateUserModules = (userId, newMods) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, modules: newMods } : u));
  };

  const toggleAccesoEse = async (u) => {
    const newVal = !u.acceso_ese;
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, acceso_ese: newVal } : x));
    try {
      await axios.put(`${API}/admin/usuarios/contratista/${u.id}/acceso-ese`,
        { enabled: newVal }, { headers });
      toast.success(newVal ? `Acceso a Gestión de Trabajadores habilitado para ${u.nombre}` : `Acceso a Gestión de Trabajadores deshabilitado para ${u.nombre}`);
    } catch (e) {
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, acceso_ese: !newVal } : x));
      toast.error(e.response?.data?.detail || 'Error al actualizar acceso a Gestión de Trabajadores');
    }
  };

  const toggleKeyUserContratista = async (u) => {
    const newVal = !u.key_user;
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, key_user: newVal } : x));
    try {
      await axios.put(`${API}/admin/usuarios/contratista/${u.id}/key-user`,
        { enabled: newVal }, { headers });
      toast.success(newVal ? `Key User habilitado para ${u.nombre}` : `Key User deshabilitado para ${u.nombre}`);
    } catch (e) {
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, key_user: !newVal } : x));
      toast.error(e.response?.data?.detail || 'Error al actualizar Key User');
    }
  };

  const toggleKeyUser = async (u) => {
    const newVal = !u.key_user;
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, key_user: newVal, key_user_gerencias: newVal ? x.key_user_gerencias : [] } : x));
    try {
      await axios.put(`${API}/admin/usuarios/mandante/${u.id}/key-user`,
        { enabled: newVal }, { headers });
      toast.success(newVal ? `Key User habilitado para ${u.nombre}` : `Key User deshabilitado para ${u.nombre}`);
      // Si se activa, abrir modal para asignar gerencias
      if (newVal) setKeyUserModal(u);
    } catch (e) {
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, key_user: !newVal } : x));
      toast.error(e.response?.data?.detail || 'Error al actualizar Key User');
    }
  };

  const togglePerfilExtra = async (u, perfil) => {
    const campo = `perfil_${perfil}`;
    const newVal = !u[campo];
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, [campo]: newVal } : x));
    try {
      await axios.put(`${API}/admin/usuarios/mandante/${u.id}/perfil/${perfil}`,
        { enabled: newVal }, { headers });
      const label = perfil === 'planificador' ? 'Planificador HS'
        : perfil === 'capacitador' ? 'Capacitador'
        : perfil === 'pases_visita' ? 'Pases de Visita'
        : 'Gestión de Trabajadores';
      toast.success(newVal ? `Perfil ${label} habilitado para ${u.nombre}` : `Perfil ${label} deshabilitado para ${u.nombre}`);
    } catch (e) {
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, [campo]: !newVal } : x));
      toast.error(e.response?.data?.detail || 'Error al actualizar perfil');
    }
  };

  const bulkToggle = async (moduleId, action) => {
    const modName = MODULES.find(m => m.id === moduleId)?.name || moduleId;
    const actionLabel = action === 'add' ? 'ACTIVAR' : 'DESACTIVAR';
    setConfirmData({
      title: `${actionLabel} modulo`,
      message: `${actionLabel} modulo "${modName}" para todos los ${total} usuarios filtrados?`,
      onConfirm: async () => {
        setConfirmData(null);
        setBulkLoading(true);
        try {
          const body = { module_id: moduleId, action, search: debouncedSearch };
          if (filterEmpresa) body.empresa_id = filterEmpresa;
          const res = await axios.post(`${API}/admin/usuarios/${userType}/bulk-module`, body, { headers });
          toast.success(res.data.message);
          fetchUsers();
        } catch { toast.error('Error en operacion masiva'); }
        setBulkLoading(false);
      }
    });
  };

  const openNew = () => {
    setEditing(null);
    setForm({ nombre: '', apellido: '', rut: '', email: '', clave: '' });
    setShowModal(true);
  };

  const openEdit = (u) => {
    const parts = (u.nombre || '').split(' ');
    const nombre = parts[0] || '';
    const apellido = parts.slice(1).join(' ') || '';
    setEditing(u);
    setForm({ nombre, apellido, rut: u.rut || '', email: u.email || '', clave: '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nombre) { toast.error('Nombre es obligatorio'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/admin/usuarios/${userType}/${editing.id}`, form, { headers });
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/admin/usuarios/${userType}`, form, { headers });
        toast.success('Usuario creado');
      }
      setShowModal(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = (u) => {
    setConfirmData({
      title: 'Desactivar usuario',
      message: `Desactivar a ${u.nombre}?`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/usuarios/${userType}/${u.id}`, { headers });
          toast.success('Usuario desactivado');
          fetchUsers();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmData(null);
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, RUT o email..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B919B]/20"
            data-testid={`search-${userType}`} />
        </div>
        {userType === 'contratista' && (
          <select value={filterEmpresa} onChange={e => { setFilterEmpresa(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-empresa-usuarios">
            <option value="">Todas las empresas</option>
            {empresas.map(e => <option key={e.id} value={e.id}>{e.razon_social}</option>)}
          </select>
        )}
        <span className="text-xs text-slate-400">{total} usuario(s)</span>
        <button onClick={openNew} className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] transition-colors" data-testid={`nuevo-${userType}-btn`}>
          <Plus className="w-4 h-4" /> Nuevo Usuario
        </button>
      </div>

      {/* Bulk module actions */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="bulk-module-bar">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Marcaje masivo por modulo ({total} usuarios filtrados)</p>
        <div className="flex flex-wrap gap-2">
          {MODULES.map(m => (
            <div key={m.id} className="flex items-center gap-0.5" data-testid={`bulk-mod-${m.id}`}>
              <button onClick={() => bulkToggle(m.id, 'add')} disabled={bulkLoading}
                className="flex items-center gap-1 px-2 py-1 rounded-l-md text-[10px] font-medium text-white hover:opacity-80 disabled:opacity-40 transition-opacity"
                style={{ backgroundColor: m.color }} title={`Activar ${m.name} para todos`}>
                <Check className="w-3 h-3" />{m.id}
              </button>
              <button onClick={() => bulkToggle(m.id, 'remove')} disabled={bulkLoading}
                className="px-1.5 py-1 rounded-r-md text-[10px] font-bold bg-slate-200 text-slate-500 hover:bg-red-100 hover:text-red-500 disabled:opacity-40 transition-colors"
                title={`Desactivar ${m.name} para todos`}>&times;</button>
            </div>
          ))}
        </div>
        {bulkLoading && <p className="text-xs text-[#1B919B] animate-pulse">Procesando...</p>}
      </div>

      <div className="flex flex-wrap gap-2 px-1">
        {MODULES.map(m => (
          <div key={m.id} className="flex items-center gap-1">
            <div className="w-4 h-4 rounded text-[9px] font-bold text-white flex items-center justify-center" style={{ backgroundColor: m.color }}>{m.id}</div>
            <span className="text-[10px] text-slate-500">{m.name}</span>
          </div>
        ))}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid={`table-${userType}`}>
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase whitespace-nowrap">RUT</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Nombre</th>
                {userType === 'contratista' && <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Empresa</th>}
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Email</th>
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Clave</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Modulos</th>
                {userType === 'mandante' && <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase whitespace-nowrap">Key User</th>}
                {userType === 'mandante' && <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase whitespace-nowrap">Perfiles extra</th>}
                {userType === 'contratista' && <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase whitespace-nowrap">Gestión de Trabajadores</th>}
                {userType === 'contratista' && <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase whitespace-nowrap">Key User</th>}
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase w-20">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={userType === 'contratista' ? 10 : 9} className="px-3 py-8 text-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1B919B] mx-auto" />
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={userType === 'contratista' ? 10 : 9} className="px-3 py-8 text-center text-sm text-slate-400">No se encontraron usuarios</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{u.rut}</td>
                  <td className="px-3 py-2 text-xs font-medium text-slate-800">{u.nombre}</td>
                  {userType === 'contratista' && <td className="px-3 py-2 text-xs text-slate-500">{u.empresa}</td>}
                  <td className="px-3 py-2 text-xs text-slate-500">{u.email || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    {u.clave ? (
                      <span className="text-xs font-mono text-slate-700">{u.clave}</span>
                    ) : (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${u.has_clave ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                        {u.has_clave ? 'Asignada' : 'Sin clave'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <ModuleToggle userModules={u.modules || []} userId={u.id} userType={userType} token={token} onUpdate={(m) => updateUserModules(u.id, m)} />
                  </td>
                  {userType === 'mandante' && (
                    <td className="px-3 py-2 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleKeyUser(u)}
                          data-testid={`toggle-keyuser-${u.id}`}
                          title={u.key_user ? 'Deshabilitar Key User' : 'Habilitar Key User'}
                          className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full transition-colors ${u.key_user ? 'bg-amber-600' : 'bg-slate-200'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${u.key_user ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                        {u.key_user && (
                          <button
                            type="button"
                            onClick={() => setKeyUserModal(u)}
                            data-testid={`config-keyuser-${u.id}`}
                            className="text-[9px] font-semibold text-amber-700 hover:text-amber-900 hover:underline"
                          >
                            {(u.key_user_gerencias || []).length} gerencia{(u.key_user_gerencias || []).length === 1 ? '' : 's'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                  {userType === 'mandante' && (
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-3" data-testid={`perfiles-extra-${u.id}`}>
                        {[
                          { id: 'planificador', label: 'Plan. Lid.', color: 'bg-sky-600' },
                          { id: 'capacitador',  label: 'Capacit.',  color: 'bg-violet-600' },
                          { id: 'ese',          label: 'G. Trab.',  color: 'bg-emerald-600' },
                          { id: 'pases_visita', label: 'P. Visita', color: 'bg-teal-600' },
                          { id: 'equipo_hs',    label: 'Equipo HS', color: 'bg-red-600' },
                        ].map(p => {
                          const campo = `perfil_${p.id}`;
                          const active = !!u[campo];
                          return (
                            <div key={p.id} className="flex flex-col items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => togglePerfilExtra(u, p.id)}
                                data-testid={`toggle-${p.id}-${u.id}`}
                                title={active ? `Deshabilitar ${p.label}` : `Habilitar ${p.label}`}
                                className={`relative inline-flex h-4 w-8 shrink-0 items-center rounded-full transition-colors ${active ? p.color : 'bg-slate-200'}`}
                              >
                                <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                              </button>
                              <span className="text-[8px] font-semibold text-slate-500">{p.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  )}
                  {userType === 'contratista' && (
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleAccesoEse(u)}
                        data-testid={`toggle-ese-${u.id}`}
                        title={u.acceso_ese ? 'Deshabilitar Gestión de Trabajadores' : 'Habilitar Gestión de Trabajadores'}
                        className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full transition-colors ${u.acceso_ese ? 'bg-teal-600' : 'bg-slate-200'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${u.acceso_ese ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                  )}
                  {userType === 'contratista' && (
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleKeyUserContratista(u)}
                        data-testid={`toggle-keyuser-contratista-${u.id}`}
                        title={u.key_user ? 'Deshabilitar Key User de la empresa' : 'Habilitar Key User de la empresa'}
                        className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full transition-colors ${u.key_user ? 'bg-amber-600' : 'bg-slate-200'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${u.key_user ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                  )}
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEdit(u)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" data-testid={`edit-${userType}-${u.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(u)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`delete-${userType}-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} limit={LIMIT} onPageChange={setPage} testId={`pagination-${userType}`} />

      {showModal && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Nombre *</label>
                <input type="text" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-nombre" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Apellido</label>
                <input type="text" value={form.apellido} onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-apellido" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">RUT</label>
              <input type="text" value={form.rut} onChange={e => setForm(f => ({ ...f, rut: e.target.value }))}
                placeholder="12.345.678-9" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-rut" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">{editing ? 'Clave (dejar vacio para mantener)' : 'Clave'}</label>
              <input type="text" value={form.clave} onChange={e => setForm(f => ({ ...f, clave: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-clave" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm bg-[#a05e49] text-white rounded-lg hover:bg-[#8b4f3d]" data-testid="modal-save-btn">{editing ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      , document.body)}

      <ConfirmModal open={!!confirmData} title={confirmData?.title || ''} message={confirmData?.message || ''}
        confirmLabel="Si, continuar" variant="danger" onConfirm={confirmData?.onConfirm} onCancel={() => setConfirmData(null)} />

      {keyUserModal && (
        <KeyUserGerenciasModal
          user={keyUserModal}
          token={token}
          onClose={(updatedIds) => {
            if (updatedIds) {
              setUsers(prev => prev.map(x => x.id === keyUserModal.id ? { ...x, key_user_gerencias: updatedIds, key_user: updatedIds.length > 0 ? true : x.key_user } : x));
            }
            setKeyUserModal(null);
          }}
        />
      )}
    </div>
  );
}

function KeyUserGerenciasModal({ user, token, onClose }) {
  const [gerencias, setGerencias] = useState([]);
  const [selected, setSelected] = useState(new Set(user.key_user_gerencias || []));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [gerRes, asignadasRes] = await Promise.all([
          axios.get(`${API}/gerencias`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/usuarios/mandante/${user.id}/key-user-gerencias`,
            { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setGerencias(gerRes.data || []);
        setSelected(new Set(asignadasRes.data || []));
      } catch {
        toast.error('Error al cargar gerencias');
      }
      setLoading(false);
    })();
  }, [user.id, token]);

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const ids = Array.from(selected);
      await axios.put(`${API}/admin/usuarios/mandante/${user.id}/key-user-gerencias`,
        { gerencia_ids: ids },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Gerencias actualizadas');
      onClose(ids);
    } catch {
      toast.error('Error al guardar gerencias');
    }
    setSaving(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => onClose()} data-testid="keyuser-modal-overlay">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()} data-testid="keyuser-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">Gerencias del Key User</h2>
            <p className="text-xs text-slate-500 mt-0.5">{user.nombre}</p>
          </div>
          <button onClick={() => onClose()} className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="px-5 py-3 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-xs text-slate-400 text-center py-6">Cargando...</p>
          ) : gerencias.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No hay gerencias activas</p>
          ) : (
            <div className="space-y-1">
              {gerencias.map(g => {
                const checked = selected.has(g.id);
                return (
                  <label key={g.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${checked ? 'bg-amber-50 border border-amber-200' : 'hover:bg-slate-50 border border-transparent'}`}
                    data-testid={`keyuser-gerencia-${g.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(g.id)}
                      className="w-4 h-4 rounded text-amber-600 border-slate-300 focus:ring-amber-500"
                    />
                    <span className="text-xs text-slate-800 flex-1">{g.nombre}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
          <span className="text-[11px] text-slate-500">{selected.size} seleccionada{selected.size === 1 ? '' : 's'}</span>
          <div className="flex gap-2">
            <button onClick={() => onClose()} className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900">Cancelar</button>
            <button onClick={save} disabled={saving}
              data-testid="keyuser-save-btn"
              className="px-4 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ==================== MAIN COMPONENT ==================== */
export default function GestionUsuarios() {
  const { token } = useAuth();
  const [tab, setTab] = useState('especiales');
  const [showTipoConfig, setShowTipoConfig] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Gestion de Usuarios</h1>
          <p className="text-sm text-slate-500">Administre usuarios y modulos de acceso</p>
        </div>
        <button onClick={() => setShowTipoConfig(true)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors"
          data-testid="config-tipo-btn">
          <Shield className="w-4 h-4" /> Modulos TystaCore para Gestión de Trabajadores
        </button>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="usuarios-tabs">
        <button onClick={() => setTab('especiales')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'especiales' ? 'bg-[#a05e49] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`} data-testid="tab-especiales">
          <Shield className="w-4 h-4" /> Especiales
        </button>
        <button onClick={() => setTab('mandante')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'mandante' ? 'bg-[#1B919B] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`} data-testid="tab-mandante">
          <Users className="w-4 h-4" /> Mandante
        </button>
        <button onClick={() => setTab('contratista')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'contratista' ? 'bg-[#a05e49] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`} data-testid="tab-contratista">
          <Building2 className="w-4 h-4" /> Contratistas
        </button>
      </div>

      {tab === 'especiales' && <EspecialesTable token={token} />}
      {tab === 'mandante' && <UserTable userType="mandante" token={token} />}
      {tab === 'contratista' && <UserTable userType="contratista" token={token} />}

      {showTipoConfig && <TipoConfigModal token={token} onClose={() => setShowTipoConfig(false)} />}
    </div>
  );
}

function TipoConfigModal({ token, onClose }) {
  const [enabledIds, setEnabledIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/tipo-usuario-modulos/ese`, { headers: { Authorization: `Bearer ${token}` } });
        setEnabledIds((r.data || []).filter(x => typeof x === 'number'));
      } catch { toast.error('Error al cargar configuracion'); }
      setLoading(false);
    })();
  }, [token]);

  const toggle = (id) => {
    setEnabledIds(curr => curr.includes(id) ? curr.filter(x => x !== id) : [...curr, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tipo-usuario-modulos/ese`,
        { module_ids: enabledIds },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Modulos actualizados para Gestión de Trabajadores');
      onClose();
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  return createPortal(
    <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl" onClick={ev => ev.stopPropagation()} data-testid="tipo-config-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Modulos TystaCore para Gestión de Trabajadores</h2>
            <p className="text-xs text-slate-500 mt-0.5">Esta configuración aplica a TODOS los usuarios con perfil de Gestión de Trabajadores.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">×</button>
        </div>
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {loading ? <p className="text-sm text-slate-400 text-center py-8">Cargando...</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {MODULES.filter(m => typeof m.id === 'number').map(m => {
                const active = enabledIds.includes(m.id);
                return (
                  <label key={m.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${active ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                    data-testid={`tipo-module-${m.id}`}>
                    <input type="checkbox" checked={active} onChange={() => toggle(m.id)} className="w-4 h-4 accent-emerald-600" />
                    <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: m.color }}>{m.id}</div>
                    <span className="text-sm text-slate-700 flex-1">{m.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <p className="text-xs text-slate-500">{enabledIds.length} modulos seleccionados</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="save-tipo-config">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}
